import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment-timezone';
import { DATETIME_FORMATS } from '../../constants/datetime-formats';

const TIMEZONE_VALUES = ['Asia/Seoul', 'Asia/Ho_Chi_Minh', 'America/New_York'];

const SESSION_DURATION_MINUTES = 30;

const TIME_PERIODS = [
    { key: 'morning',   labelKey: 'morning',   icon: 'pi pi-sun',  hours: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] },
    { key: 'afternoon', labelKey: 'afternoon',  icon: 'pi pi-sun',  hours: [12, 13, 14, 15, 16, 17] },
    { key: 'evening',   labelKey: 'evening',    icon: 'pi pi-moon', hours: [18, 19, 20, 21, 22, 23] },
];

function addSessionDuration(hour, minute) {
    const total = hour * 60 + minute + SESSION_DURATION_MINUTES;
    const endHour = Math.floor(total / 60) % 24;
    const endMinute = total % 60;
    return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
}

function assignSessionLabels(slots) {
    return slots.map((slot, i) => ({
        ...slot,
        sessionLabel: String(i + 1),
    }));
}

function TimeSlotButton({ date, startTime, endTime, bookedSlots, selectedSlots, onToggle, isUnavailable, isLocked }) {
    const isBooked = bookedSlots.some(
        s => s.date === date && s.startTime === startTime
    );
    const selectedEntry = selectedSlots.find(
        s => s.date === date && s.startTime === startTime
    );
    const isSelected = !!selectedEntry;
    const isDisabled = isBooked || isUnavailable || isLocked;

    let btnClass = 'csp-slot-btn keduall-typography text-14-normal-400';
    if (isBooked)            btnClass += ' csp-slot-btn--reserved';
    else if (isUnavailable)  btnClass += ' csp-slot-btn--booked';
    else if (isLocked)       btnClass += ' csp-slot-btn--locked';
    else if (isSelected)     btnClass += ' csp-slot-btn--selected';
    else                     btnClass += ' csp-slot-btn--available';

    return (
        <div className="csp-slot-wrapper">
            <button
                className={btnClass}
                disabled={isDisabled}
                onClick={() => !isDisabled && onToggle(date, startTime, endTime)}
            >
                {startTime} - {endTime}
            </button>
            {isSelected && (
                <span className="csp-slot-badge">{selectedEntry.sessionLabel}</span>
            )}
        </div>
    );
}

export default function ClassSchedulePicker({ props }) {
    const {
        translations = {},
        trainerAvailabilities = null,
        reservations = [],
        trainerSchedules = [],
        userPublicId = null,
        onConfirmSchedule,
        onCancelSchedule,
        isConfirmed = false,
    } = props ?? {};

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedTimezone, setSelectedTimezone] = useState(TIMEZONE_VALUES[0]);
    const [currentWeekStart, setCurrentWeekStart] = useState(
        () => moment().tz(TIMEZONE_VALUES[0]).startOf('isoWeek')
    );
    const [selectedDay, setSelectedDay] = useState(
        () => moment().tz(TIMEZONE_VALUES[0]).format(DATETIME_FORMATS.YYYY_MM_DD)
    );

    const timezoneOptions = useMemo(() => ([
        { label: translations.timezoneKorea, value: TIMEZONE_VALUES[0] },
        { label: translations.timezoneVietnam, value: TIMEZONE_VALUES[1] },
        { label: translations.timezoneUs, value: TIMEZONE_VALUES[2] },
    ]), [translations.timezoneKorea, translations.timezoneVietnam, translations.timezoneUs]);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const didInitFromReservations = useRef(false);

    const displaySlots = useMemo(() => assignSessionLabels(
        selectedSlots.map(slot => {
            const m = moment.unix(slot.time).tz(selectedTimezone);
            return {
                time: slot.time,
                date: m.format(DATETIME_FORMATS.YYYY_MM_DD),
                startTime: m.format('HH:mm'),
                endTime: addSessionDuration(m.hours(), m.minutes()),
                dayLabel: m.format('ddd'),
                sessionLabel: '',
            };
        })
    ), [selectedSlots, selectedTimezone]);

    useEffect(() => {
        const startStr = currentWeekStart.format('YYYY-MM-DD');
        const endStr   = currentWeekStart.clone().add(6, 'days').format('YYYY-MM-DD');
        if (selectedDay < startStr || selectedDay > endStr) {
            setSelectedDay(startStr);
        }
    }, [currentWeekStart, selectedDay]);

    const weekDays = useMemo(() =>
        Array.from({ length: 7 }).map((_, i) => {
            const m = currentWeekStart.clone().add(i, 'days');
            return {
                dateStr:  m.format(DATETIME_FORMATS.YYYY_MM_DD),
                dayLabel: m.format('ddd'),
                dateNum:  m.format('DD'),
            };
        }),
    [currentWeekStart]);

    const weekRangeLabel = useMemo(() => {
        const fmt = `${DATETIME_FORMATS.YYYY_MM_DD} (ddd)`;
        const start = currentWeekStart.format(fmt);
        const end   = currentWeekStart.clone().add(6, 'days').format(fmt);
        return `${start} – ${end}`;
    }, [currentWeekStart]);

    const goPrevWeek = () => setCurrentWeekStart(p => p.clone().subtract(1, 'week'));
    const goNextWeek = () => setCurrentWeekStart(p => p.clone().add(1, 'week'));

    const handleSlotToggle = useCallback((date, startTime) => {
        const parseFormat = `${DATETIME_FORMATS.YYYY_MM_DD} ${DATETIME_FORMATS.HH_mm}`;
        const time = moment.tz(`${date} ${startTime}`, parseFormat, selectedTimezone).unix();
        setSelectedSlots(prev => {
            const existingIdx = prev.findIndex(s => s.time === time);
            const newSlots = existingIdx !== -1
                ? prev.filter((_, i) => i !== existingIdx)
                : [...prev, { time, sessionLabel: '' }];
            return assignSessionLabels(newSlots);
        });
    }, [selectedTimezone]);

    const allBookedSlots = useMemo(() => [
        ...(userPublicId ? reservations.filter(r => r.userPublicId !== userPublicId) : [])
            .map(r => {
                const m = moment.unix(r.time).tz(selectedTimezone);
                return { date: m.format(DATETIME_FORMATS.YYYY_MM_DD), startTime: m.format('HH:mm') };
            }),
        ...(trainerSchedules ?? []).map(s => {
            const m = moment.unix(s.startTime).tz(selectedTimezone);
            return { date: m.format(DATETIME_FORMATS.YYYY_MM_DD), startTime: m.format('HH:mm') };
        }),
    ], [reservations, userPublicId, trainerSchedules, selectedTimezone]);

    useEffect(() => {
        if (!reservations.length || !userPublicId) return;
        if (didInitFromReservations.current) return;
        didInitFromReservations.current = true;
        const mySlots = reservations
            .filter(r => r.userPublicId === userPublicId)
            .map(r => ({ time: r.time, sessionLabel: '' }));
        setSelectedSlots(assignSessionLabels(mySlots));
    }, [reservations, userPublicId]);

    const handleConfirm = useCallback(async () => {
        const times = selectedSlots.map(slot => slot.time);
        setIsSubmitting(true);
        try {
            await onConfirmSchedule?.(times);
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedSlots, onConfirmSchedule]);

    const handleCancel = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await onCancelSchedule?.();
        } finally {
            setIsSubmitting(false);
        }
    }, [onCancelSchedule]);

    const handleClearAll = () => setSelectedSlots([]);

    const availableSlotSet = useMemo(() => {
        if (!Array.isArray(trainerAvailabilities)) return null;
        const set = new Set();
        trainerAvailabilities.forEach(({ time }) => {
            const m = moment.unix(time).tz(selectedTimezone);
            set.add(`${m.format('YYYY-MM-DD')} ${m.format('HH:mm')}`);
        });
        return set;
    }, [trainerAvailabilities, selectedTimezone]);

    const isTrainerAvailable = useCallback((dateStr, startTime) => {
        if (!availableSlotSet) return true;
        return availableSlotSet.has(`${dateStr} ${startTime}`);
    }, [availableSlotSet]);

    const isSlotTooSoon = useCallback((dateStr, startTime) => {
        const slotMoment = moment.tz(`${dateStr} ${startTime}`, `${DATETIME_FORMATS.YYYY_MM_DD} ${DATETIME_FORMATS.HH_mm}`, selectedTimezone);
        return slotMoment.isBefore(moment().tz(selectedTimezone));
    }, [selectedTimezone]);

    const handleRemoveSlot = (target) => {
        setSelectedSlots(prev => {
            const newSlots = prev.filter(s => s.time !== target.time);
            return assignSessionLabels(newSlots);
        });
    };

    return (
        <div className="csp-root">
            <div className="csp-week-header flex align-items-center justify-content-between mb-3">
                <div className="flex flex-row align-items-center justify-content-start gap-3">
                    <Button
                        className="p-button-text p-button-secondary csp-nav-btn"
                        icon="pi pi-chevron-left"
                        onClick={goPrevWeek}
                    />
                    <Button
                        className="p-button-text p-button-secondary csp-nav-btn"
                        icon="pi pi-chevron-right"
                        onClick={goNextWeek}
                    />
                    <span className="keduall-typography text-16-semibold-600 text-default-txt">
                        {weekRangeLabel}
                    </span>
                </div>
                <Dropdown
                    value={selectedTimezone}
                    options={timezoneOptions}
                    onChange={e => setSelectedTimezone(e.value)}
                    className="csp-tz-dropdown keduall-typography text-14-normal-400"
                />
            </div>

            <div className="csp-section-label keduall-typography text-16-semibold-600 text-default-txt mb-2">
                {translations.selectADay}
            </div>
            <div className="csp-day-selector flex gap-2 mb-4">
                {weekDays.map(day => (
                    <button
                        key={day.dateStr}
                        className={`csp-day-btn${day.dateStr === selectedDay ? ' csp-day-btn--selected' : ''}`}
                        onClick={() => setSelectedDay(day.dateStr)}
                    >
                        <span className="csp-day-name keduall-typography text-12-normal-400">
                            {day.dayLabel}
                        </span>
                        <span className="csp-day-date keduall-typography text-14-semibold-600">
                            {day.dateNum}
                        </span>
                    </button>
                ))}
            </div>

            <div className="csp-time-section">
                <div className="flex align-items-center justify-content-between mb-3">
                    <span className="keduall-typography text-16-semibold-600 text-default-txt">
                        {translations.chooseATime}
                    </span>
                    <div className="csp-legend flex align-items-center gap-3">
                        <span className="csp-legend-item csp-legend--available keduall-typography text-14-normal-400">
                            {translations.available}
                        </span>
                        <span className="csp-legend-item csp-legend--booked keduall-typography text-14-normal-400">
                            {translations.booked}
                        </span>
                    </div>
                </div>

                {TIME_PERIODS.map(period => (
                    <div key={period.key} className="csp-period mb-4">
                        <div className="csp-period-header flex align-items-center gap-2 mb-2">
                            <i className={period.icon} />
                            <span className="keduall-typography text-14-semibold-600 text-default-txt">
                                {translations[period.labelKey]}
                            </span>
                        </div>
                        <div className="csp-slots-row">
                            {period.hours.flatMap(h => [0, SESSION_DURATION_MINUTES].map(startMinute => {
                                const hStr = String(h).padStart(2, '0');
                                const startTime = `${hStr}:${String(startMinute).padStart(2, '0')}`;
                                const endTime   = addSessionDuration(h, startMinute);
                                return (
                                    <TimeSlotButton
                                        key={startTime}
                                        date={selectedDay}
                                        startTime={startTime}
                                        endTime={endTime}
                                        bookedSlots={allBookedSlots}
                                        selectedSlots={displaySlots}
                                        onToggle={handleSlotToggle}
                                        isUnavailable={!isTrainerAvailable(selectedDay, startTime) || isSlotTooSoon(selectedDay, startTime)}
                                        isLocked={isConfirmed}
                                    />
                                );
                            }))}
                        </div>
                    </div>
                ))}
            </div>

            {displaySlots.length > 0 && (
                <div className="csp-selected-section">
                    <div className="csp-selected-header flex align-items-center justify-content-between mb-3">
                        <span className="keduall-typography heading-h5-semibold-600 text-default-txt">
                            {translations.selectedSchedules} ({displaySlots.length})
                        </span>
                        {!isConfirmed && (
                            <button
                                className="csp-clear-all keduall-typography text-14-normal-400"
                                onClick={handleClearAll}
                            >
                                {translations.clearAll}
                            </button>
                        )}
                    </div>
                    {translations.canAddMore && (
                        <p className="keduall-typography text-14-normal-400 text-default-txt mb-3">
                            {translations.canAddMore}
                        </p>
                    )}
                    <div className="csp-selected-grid">
                        {displaySlots.map(slot => (
                            <div
                                key={slot.time}
                                className="csp-selected-tag"
                            >
                                <span className="keduall-typography text-14-normal-400 text-default-txt">
                                    {slot.date.replace(/-/g, '.')} · {slot.dayLabel} · {slot.startTime}-{slot.endTime}
                                </span>
                                {!isConfirmed && (
                                    <button
                                        className="csp-remove-btn"
                                        onClick={() => handleRemoveSlot(slot)}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex w-full justify-content-center align-items-center mt-4">
                <Button
                    className="keduall-buttons button-filled-default-normal-primary keduall-typography text-16-semibold-600"
                    disabled={!isConfirmed && selectedSlots.length === 0}
                    loading={isSubmitting}
                    onClick={isConfirmed ? handleCancel : handleConfirm}
                >
                    {isConfirmed ? translations.updateScheduleButtonLabel : translations.confirmSchedule}
                </Button>
            </div>
        </div>
    );
}
